<template>
  <div>
    <loading-spinner v-if="loading" />
    <template v-else-if="screensaver">
      <h3>
        <span
          v-label-key="
            screensaver.id ? 'SCREENSAVER_EDIT' : 'SCREENSAVER_CREATE'
          "
        ></span
        >&nbsp;
        <b-button variant="primary" @click="save()">
          <span v-label-key="'SAVE'">Save</span>
        </b-button>
      </h3>
      <b-container>
        <b-form name="editorForm">
          <b-row>
            <b-col md="4">
              <!-- Name -->
              <b-form-group :label="`${getLabel('NAME')}*`">
                <b-form-input
                  v-model="screensaver.name"
                  type="text"
                  required
                ></b-form-input>
              </b-form-group>
              <hr />
            </b-col>
          </b-row>
          <b-row>
            <b-col md="4">
              <!-- Timeout -->
              <b-form-group
                :label="
                  `${getLabel('SCREENSAVER_TIMEOUT')}* (${getLabel(
                    'TIME_IN_SECONDS',
                  )})`
                "
                :description="getLabel('SCREENSAVER_TIMEOUT_DESCRIPTION')"
              >
                <b-form-input
                  v-model="screensaver.waittime"
                  type="number"
                  required
                ></b-form-input>
              </b-form-group>
              <hr />
            </b-col>
          </b-row>
          <h4 v-label-key="'MANAGE_IMAGES'"></h4>
          <p v-label-key="'MANAGE_IMAGES_DESCRIPTION'"></p>
          <div v-for="(image, $index) in screensaver.images" :key="image.src">
            <b-row>
              <b-col md="4">
                <!-- Image -->
                <b-form-group :label="$index + 1 + '.'">
                  <file-input
                    accept="image/*"
                    label-extra="Image"
                    v-model="image.src"
                    required
                    folder="screensaver/src"
                    @input="onImageChange($index, $event)"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <!-- Duration -->
                <b-form-group
                  :label="
                    `${getLabel('SCREENSAVER_DURATION')} (${getLabel(
                      'TIME_IN_SECONDS',
                    )})`
                  "
                  :description="getLabel('SCREENSAVER_DURATION_DESCRIPTION')"
                >
                  <b-form-input
                    v-model="image.duration"
                    type="number"
                    required
                  ></b-form-input>
                </b-form-group>
                <b-button v-show="canMoveUp($index)" @click="moveUp($index)"
                  ><span v-label-key="'MOVE_UP'"></span
                ></b-button>
                &nbsp;
                <b-button v-show="canMoveDown($index)" @click="moveDown($index)"
                  ><span v-label-key="'MOVE_DOWN'"></span
                ></b-button>
              </b-col>
            </b-row>
            <hr />
          </div>
        </b-form>

        <br />
        <b-button variant="primary" @click="addImage"
          ><span v-label-key="'ADD_IMAGE'"></span>
        </b-button>
        <br />
        <br />

        <b-modal
          id="image-modal"
          :title="getLabel('SELECT_IMAGE')"
          :ok-title="getLabel('OK')"
          :cancel-title="getLabel('CANCEL')"
          @ok="okAddImage"
          @cancel="cancelAddImage"
        >
          <file-input
            accept="image/*"
            label-extra="Image"
            v-model="newImage"
            folder="screensaver/src"
          />
        </b-modal>
      </b-container>
    </template>
  </div>
</template>

<script>
import $api from '@/services/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import FileInput from '@/components/FileInput';
import { mapGetters } from 'vuex';

export default {
  components: {
    FileInput,
    LoadingSpinner,
  },

  data() {
    return {
      screensaver: null,
      newImage: null,
      loading: false,
    };
  },

  computed: {
    ...mapGetters('translations', {
      getLabel: 'getLabel',
    }),

    level() {
      if (this.$route.path.includes('subdi')) return 'subdistributor';
      return 'distributor';
    },
    canMoveUp() {
      return ($index) => {
        if (!this.screensaver || !this.screensaver.images) return false;
        if ($index < 1) return false;
        return true;
      };
    },
    canMoveDown() {
      return ($index) => {
        if (!this.screensaver || !this.screensaver.images) return false;
        if (this.screensaver.images.length - 2 < $index) return false;
        return true;
      };
    },
  },

  created() {
    this.refreshData();
  },

  methods: {
    async refreshData() {
      if (this.$route.query.id) {
        this.loading = true;
        try {
          this.screensaver = await $api.get(
            `/v1/dashboard/screensavers/${this.$route.query.id}/${this.level}s/${this.$route.params.id}`,
          );
        } catch (e) {
          console.error(e);
          alert('Error loading screensaver');
        }
        this.loading = false;
      } else {
        this.screensaver = {
          timeout: 120,
          images: [],
        };
      }
    },

    async addImage() {
      this.newImage = null;
      this.$bvModal.show('image-modal');
    },

    async okAddImage() {
      if (!this.newImage) return;
      this.screensaver.images.push({ duration: 5, src: this.newImage });
    },
    cancelAddImage() {
      this.newImage = null;
    },

    removeImage(index) {
      this.screensaver.images.splice(index, 1);
    },

    moveUp(index) {
      const image = this.screensaver.images.splice(index, 1)[0];
      this.screensaver.images.splice(index - 1, 0, image);
    },
    moveDown(index) {
      const image = this.screensaver.images.splice(index, 1)[0];
      this.screensaver.images.splice(index + 1, 0, image);
    },
    onImageChange($index, $event) {
      if (!$event) this.removeImage($index);
    },

    async save() {
      this.loading = true;
      try {
        this.screensaver.distributor_id = this.$route.params.id;
        if (this.screensaver.id) {
          await $api.put(
            `/v1/dashboard/screensavers/${this.screensaver.id}`,
            this.screensaver,
          );
        } else {
          await $api.post('/v1/dashboard/screensavers', this.screensaver);
        }
        this.$router.push({
          name: 'distributor-screensavers-list-tab',
        });
      } catch (e) {
        alert('Error saving');
      }
      this.loading = false;
    },
  },
};
</script>
<style scoped></style>
